// src/modules/ida/views/Documents.test.tsx
//
// Unit tests for Documents view utility functions and components.

import "@testing-library/jest-dom";
import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import type { IDADocument } from "../common/types";
import Documents, {
  compareDocumentsForSort,
  DEFAULT_DOCUMENT_SORT,
  DocumentSortHeader,
  DocumentSortState,
  getNextDocumentSort,
  DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS,
} from "./Documents";

const mockWorkspaceContext = jest.fn();
const mockTriggerSharePointSync = jest.fn();
const mockListDocuments = jest.fn();
const mockListFolders = jest.fn();
const mockListExportJobs = jest.fn();
const mockUploadDocumentVersion = jest.fn();
const mockGetVersionHistory = jest.fn();
const mockNoop = jest.fn();

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => mockWorkspaceContext(),
}));

jest.mock("../../../services/api", () => ({
  useQuotaStatus: () => ({ persistentExceeded: false }),
}));

jest.mock("../services/api", () => ({
  useListDocuments: () => mockListDocuments,
  useUploadDocument: () => mockNoop,
  useDeleteDocument: () => mockNoop,
  useDeleteDocuments: () => mockNoop,
  useDownloadDocument: () => mockNoop,
  useGetVersionHistory: () => mockGetVersionHistory,
  useRestoreVersion: () => mockNoop,
  useGetDocumentForEdit: () => mockNoop,
  useSaveDocumentContent: () => mockNoop,
  useUploadDocumentVersion: () => mockUploadDocumentVersion,
  useStartExportZip: () => mockNoop,
  useGetExportStatus: () => mockNoop,
  useListExportJobs: () => mockListExportJobs,
  useDownloadExport: () => mockNoop,
  useDismissExportJob: () => mockNoop,
  useMoveDocuments: () => mockNoop,
  useListFolders: () => mockListFolders,
  useCreateFolder: () => mockNoop,
  useDeleteFolder: () => mockNoop,
  useSharePointSync: () => mockTriggerSharePointSync,
}));

jest.mock("../components/DocumentEditorModal", () => ({
  __esModule: true,
  default: () => null,
}));

jest.mock("../components/DocumentPreviewModal", () => ({
  __esModule: true,
  default: () => null,
}));

jest.mock("../components/VersionDiffView", () => ({
  __esModule: true,
  default: () => null,
}));

const buildDocument = (
  overrides: Partial<IDADocument> & { id: string; filename: string },
): IDADocument => ({
  workspace_id: "workspace-1",
  folder: "/",
  token_count: 0,
  file_size: 0,
  source_type: "upload",
  uploaded_at: "2024-01-01T00:00:00Z",
  index_status: "pending",
  backend: "local",
  ...overrides,
});

const sortDocumentNames = (
  documents: IDADocument[],
  sortState: DocumentSortState,
): string[] =>
  [...documents]
    .sort((a, b) => compareDocumentsForSort(a, b, sortState))
    .map((doc) => doc.filename);

beforeEach(() => {
  mockListDocuments.mockResolvedValue([]);
  mockListFolders.mockResolvedValue([]);
  mockListExportJobs.mockResolvedValue({ jobs: [] });
  mockTriggerSharePointSync.mockResolvedValue({ errors: [] });
  mockWorkspaceContext.mockReturnValue({
    workspace: { id: "workspace-1", enabled_integrations: [] },
  });
  jest.clearAllMocks();
});

describe("SharePoint mount sync", () => {
  it("does not trigger sync when the workspace has no enabled SharePoint integration", async () => {
    render(<Documents />);

    await waitFor(() =>
      expect(mockListDocuments).toHaveBeenCalledWith("workspace-1"),
    );
    expect(mockTriggerSharePointSync).not.toHaveBeenCalled();
  });

  it("triggers sync when the workspace has an enabled SharePoint integration", async () => {
    mockWorkspaceContext.mockReturnValue({
      workspace: { id: "workspace-1", enabled_integrations: ["sharepoint"] },
    });

    render(<Documents />);

    await waitFor(() =>
      expect(mockTriggerSharePointSync).toHaveBeenCalledWith("workspace-1"),
    );
  });
});

describe("Document filenames", () => {
  it("renders the filename in the Documents list", async () => {
    const filename = "visible-document-name.pdf";
    mockListDocuments.mockResolvedValue([
      buildDocument({ id: "document-1", filename, index_status: "completed" }),
    ]);

    render(<Documents />);

    expect(await screen.findByText(filename)).toBeInTheDocument();
  });
});

// ---------------------------------------------------------------------------
// Document Sort Tests
// ---------------------------------------------------------------------------

describe("Document sorting", () => {
  it("sorts names with direction and stable numeric collation", () => {
    const documents = [
      buildDocument({ id: "1", filename: "File 10.pdf" }),
      buildDocument({ id: "2", filename: "file 2.pdf" }),
      buildDocument({ id: "3", filename: "Alpha.pdf" }),
    ];

    expect(
      sortDocumentNames(documents, { column: "name", direction: "asc" }),
    ).toEqual(["Alpha.pdf", "file 2.pdf", "File 10.pdf"]);
    expect(
      sortDocumentNames(documents, { column: "name", direction: "desc" }),
    ).toEqual(["File 10.pdf", "file 2.pdf", "Alpha.pdf"]);
  });

  it("sorts numeric columns with nullish values last", () => {
    const documents = [
      buildDocument({
        id: "missing-pages",
        filename: "Missing pages.pdf",
        file_size: undefined as unknown as number,
        token_count: undefined as unknown as number,
        page_count: undefined,
        version: undefined,
      }),
      buildDocument({
        id: "small",
        filename: "Small.pdf",
        file_size: 200,
        token_count: 20,
        page_count: 2,
        version: 1,
      }),
      buildDocument({
        id: "large",
        filename: "Large.pdf",
        file_size: 100,
        token_count: 10,
        page_count: 10,
        version: 3,
      }),
    ];

    expect(
      sortDocumentNames(documents, { column: "size", direction: "asc" }),
    ).toEqual(["Large.pdf", "Small.pdf", "Missing pages.pdf"]);
    expect(
      sortDocumentNames(documents, { column: "tokens", direction: "desc" }),
    ).toEqual(["Small.pdf", "Large.pdf", "Missing pages.pdf"]);
    expect(
      sortDocumentNames(documents, { column: "pages", direction: "asc" }),
    ).toEqual(["Small.pdf", "Large.pdf", "Missing pages.pdf"]);
    expect(
      sortDocumentNames(documents, { column: "version", direction: "desc" }),
    ).toEqual(["Large.pdf", "Small.pdf", "Missing pages.pdf"]);
  });

  it("sorts uploaded timestamps and keeps invalid timestamps last", () => {
    const documents = [
      buildDocument({
        id: "invalid",
        filename: "Invalid date.pdf",
        uploaded_at: "not-a-date",
      }),
      buildDocument({
        id: "older",
        filename: "Older.pdf",
        uploaded_at: "2024-01-01T12:00:00Z",
      }),
      buildDocument({
        id: "newer",
        filename: "Newer.pdf",
        uploaded_at: "2024-01-02T12:00:00Z",
      }),
    ];

    expect(
      sortDocumentNames(documents, { column: "uploaded", direction: "desc" }),
    ).toEqual(["Newer.pdf", "Older.pdf", "Invalid date.pdf"]);
    expect(
      sortDocumentNames(documents, { column: "uploaded", direction: "asc" }),
    ).toEqual(["Older.pdf", "Newer.pdf", "Invalid date.pdf"]);
  });

  it("sorts status strings with name fallback", () => {
    const documents = [
      buildDocument({
        id: "b",
        filename: "Bravo.pdf",
        index_status: "completed",
      }),
      buildDocument({
        id: "a",
        filename: "Alpha.pdf",
        index_status: "completed",
      }),
      buildDocument({
        id: "p",
        filename: "Pending.pdf",
        index_status: "pending",
      }),
    ];

    expect(
      sortDocumentNames(documents, { column: "status", direction: "asc" }),
    ).toEqual(["Alpha.pdf", "Bravo.pdf", "Pending.pdf"]);
  });

  it("defaults new Uploaded sorts to newest first and toggles thereafter", () => {
    const uploadedSort = getNextDocumentSort(DEFAULT_DOCUMENT_SORT, "uploaded");
    expect(uploadedSort).toEqual({ column: "uploaded", direction: "desc" });
    expect(getNextDocumentSort(uploadedSort, "uploaded")).toEqual({
      column: "uploaded",
      direction: "asc",
    });
  });
});

// ---------------------------------------------------------------------------
// Sort Header Component Tests
// ---------------------------------------------------------------------------

describe("DocumentSortHeader", () => {
  const HeaderHarness = () => {
    const [sortState, setSortState] = React.useState<DocumentSortState>(
      DEFAULT_DOCUMENT_SORT,
    );

    return (
      <>
        <DocumentSortHeader
          label="Uploaded"
          column="uploaded"
          sortState={sortState}
          onSort={(column) =>
            setSortState((current) => getNextDocumentSort(current, column))
          }
        />
        <div data-testid="sort-state">
          {sortState.column}:{sortState.direction}
        </div>
      </>
    );
  };

  it("changes sort on click, toggles direction, and renders active chevrons", () => {
    render(<HeaderHarness />);

    const header = screen.getByTestId("documents-sort-header-uploaded");
    expect(screen.getByTestId("sort-state")).toHaveTextContent("name:asc");
    expect(
      screen.queryByTestId("documents-sort-uploaded-chevron-desc"),
    ).not.toBeInTheDocument();

    fireEvent.click(header);
    expect(screen.getByTestId("sort-state")).toHaveTextContent("uploaded:desc");
    expect(
      screen.getByTestId("documents-sort-uploaded-chevron-desc"),
    ).toBeInTheDocument();

    fireEvent.click(header);
    expect(screen.getByTestId("sort-state")).toHaveTextContent("uploaded:asc");
    expect(
      screen.getByTestId("documents-sort-uploaded-chevron-asc"),
    ).toBeInTheDocument();
  });
});

// ---------------------------------------------------------------------------
// Utility Function Tests
// ---------------------------------------------------------------------------

describe("Documents View Utilities", () => {
  /**
   * Format file size to human readable string.
   * Re-implementation for testing (mirrors Documents.tsx logic).
   */
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Format date to localized string.
   */
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Get file extension from filename.
   */
  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // ---------------------------------------------------------------------------
  // formatSize Tests
  // ---------------------------------------------------------------------------

  describe("formatSize", () => {
    it("formats bytes correctly", () => {
      expect(formatSize(0)).toBe("0 B");
      expect(formatSize(100)).toBe("100 B");
      expect(formatSize(1023)).toBe("1023 B");
    });

    it("formats kilobytes correctly", () => {
      expect(formatSize(1024)).toBe("1.0 KB");
      expect(formatSize(1536)).toBe("1.5 KB");
      expect(formatSize(10240)).toBe("10.0 KB");
      expect(formatSize(1024 * 1024 - 1)).toBe("1024.0 KB");
    });

    it("formats megabytes correctly", () => {
      expect(formatSize(1024 * 1024)).toBe("1.0 MB");
      expect(formatSize(1.5 * 1024 * 1024)).toBe("1.5 MB");
      expect(formatSize(10 * 1024 * 1024)).toBe("10.0 MB");
      expect(formatSize(50 * 1024 * 1024)).toBe("50.0 MB");
    });

    it("handles edge cases", () => {
      expect(formatSize(1)).toBe("1 B");
      expect(formatSize(1024 * 1024 * 1024)).toBe("1024.0 MB");
    });
  });

  // ---------------------------------------------------------------------------
  // formatDate Tests
  // ---------------------------------------------------------------------------

  describe("formatDate", () => {
    it("formats ISO date string correctly", () => {
      // Note: exact output depends on locale, so we check for key parts
      const result = formatDate("2024-01-15T10:30:00Z");
      expect(result).toMatch(/15/);
      expect(result).toMatch(/30/);
    });

    it("formats date with timezone", () => {
      const result = formatDate("2024-06-20T15:45:00+02:00");
      expect(result).toMatch(/20/);
      expect(result).toMatch(/45/);
    });

    it("handles different date formats", () => {
      const result = formatDate("2023-12-25");
      expect(result).toMatch(/25/);
    });
  });

  // ---------------------------------------------------------------------------
  // getFileExtension Tests
  // ---------------------------------------------------------------------------

  describe("getFileExtension", () => {
    it("extracts PDF extension", () => {
      expect(getFileExtension("document.pdf")).toBe("pdf");
      expect(getFileExtension("Report 2024.PDF")).toBe("pdf");
    });

    it("extracts Word document extensions", () => {
      expect(getFileExtension("report.doc")).toBe("doc");
      expect(getFileExtension("report.docx")).toBe("docx");
    });

    it("extracts text file extensions", () => {
      expect(getFileExtension("notes.txt")).toBe("txt");
      expect(getFileExtension("README.md")).toBe("md");
    });

    it("handles files with multiple dots", () => {
      expect(getFileExtension("file.name.with.dots.pdf")).toBe("pdf");
      expect(getFileExtension("archive.tar.gz")).toBe("gz");
    });

    it("handles files without extension", () => {
      expect(getFileExtension("Makefile")).toBe("makefile");
      expect(getFileExtension("README")).toBe("readme");
    });

    it("handles empty filename", () => {
      expect(getFileExtension("")).toBe("");
    });
  });

  // ---------------------------------------------------------------------------
  // File Icon Selection Tests
  // ---------------------------------------------------------------------------

  describe("File Icon Selection", () => {
    /**
     * Get icon type based on file extension.
     */
    const getIconType = (filename: string): string => {
      const ext = filename.split(".").pop()?.toLowerCase();
      switch (ext) {
        case "pdf":
          return "pdf";
        case "doc":
        case "docx":
          return "word";
        case "txt":
        case "md":
          return "text";
        default:
          return "generic";
      }
    };

    it("returns pdf icon for PDF files", () => {
      expect(getIconType("report.pdf")).toBe("pdf");
      expect(getIconType("Document.PDF")).toBe("pdf");
    });

    it("returns word icon for Word documents", () => {
      expect(getIconType("letter.doc")).toBe("word");
      expect(getIconType("proposal.docx")).toBe("word");
    });

    it("returns text icon for text files", () => {
      expect(getIconType("notes.txt")).toBe("text");
      expect(getIconType("README.md")).toBe("text");
    });

    it("returns generic icon for other files", () => {
      expect(getIconType("data.csv")).toBe("generic");
      expect(getIconType("config.json")).toBe("generic");
      expect(getIconType("image.png")).toBe("generic");
    });
  });

  // ---------------------------------------------------------------------------
  // Document Statistics Calculation Tests
  // ---------------------------------------------------------------------------

  describe("Document Statistics", () => {
    interface MockDocument {
      id: string;
      token_count: number;
      indexed_at?: string;
    }

    const calculateStats = (documents: MockDocument[]) => {
      return {
        totalDocuments: documents.length,
        totalTokens: documents.reduce((sum, doc) => sum + doc.token_count, 0),
        indexedCount: documents.filter((d) => d.indexed_at).length,
      };
    };

    it("calculates stats for empty document list", () => {
      const stats = calculateStats([]);
      expect(stats.totalDocuments).toBe(0);
      expect(stats.totalTokens).toBe(0);
      expect(stats.indexedCount).toBe(0);
    });

    it("calculates stats for single document", () => {
      const documents: MockDocument[] = [
        { id: "1", token_count: 1000, indexed_at: "2024-01-01" },
      ];
      const stats = calculateStats(documents);
      expect(stats.totalDocuments).toBe(1);
      expect(stats.totalTokens).toBe(1000);
      expect(stats.indexedCount).toBe(1);
    });

    it("calculates stats for multiple documents", () => {
      const documents: MockDocument[] = [
        { id: "1", token_count: 1000, indexed_at: "2024-01-01" },
        { id: "2", token_count: 2500, indexed_at: "2024-01-02" },
        { id: "3", token_count: 500 }, // Not indexed
      ];
      const stats = calculateStats(documents);
      expect(stats.totalDocuments).toBe(3);
      expect(stats.totalTokens).toBe(4000);
      expect(stats.indexedCount).toBe(2);
    });

    it("handles documents with zero tokens", () => {
      const documents: MockDocument[] = [
        { id: "1", token_count: 0 },
        { id: "2", token_count: 100 },
      ];
      const stats = calculateStats(documents);
      expect(stats.totalTokens).toBe(100);
    });
  });

  // ---------------------------------------------------------------------------
  // File Validation Tests
  // ---------------------------------------------------------------------------

  describe("File Validation", () => {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

    const isValidFileType = (filename: string): boolean => {
      const ext = "." + filename.split(".").pop()?.toLowerCase();
      return DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(ext);
    };

    const isValidFileSize = (size: number): boolean => {
      return size <= MAX_FILE_SIZE;
    };

    describe("isValidFileType", () => {
      it("accepts PDF files", () => {
        expect(isValidFileType("document.pdf")).toBe(true);
        expect(isValidFileType("REPORT.PDF")).toBe(true);
      });

      it("accepts Word documents", () => {
        expect(isValidFileType("letter.doc")).toBe(true);
        expect(isValidFileType("proposal.docx")).toBe(true);
      });

      it("accepts legacy spreadsheets and presentations supported by the API", () => {
        expect(isValidFileType("figures.xls")).toBe(true);
        expect(isValidFileType("slides.ppt")).toBe(true);
      });

      it("accepts text files", () => {
        expect(isValidFileType("notes.txt")).toBe(true);
        expect(isValidFileType("README.md")).toBe(true);
      });

      it("accepts data files", () => {
        expect(isValidFileType("data.csv")).toBe(true);
        expect(isValidFileType("config.json")).toBe(true);
      });

      it("accepts image files supported by the document API", () => {
        expect(isValidFileType("image.png")).toBe(true);
        expect(isValidFileType("photo.jpg")).toBe(true);
        expect(isValidFileType("photo.jpeg")).toBe(true);
        expect(isValidFileType("diagram.webp")).toBe(true);
      });

      it("rejects unsupported file types", () => {
        expect(isValidFileType("script.js")).toBe(false);
        expect(isValidFileType("archive.zip")).toBe(false);
        expect(isValidFileType("video.mp4")).toBe(false);
      });
    });

    describe("isValidFileSize", () => {
      it("accepts files under 50MB", () => {
        expect(isValidFileSize(0)).toBe(true);
        expect(isValidFileSize(1024)).toBe(true);
        expect(isValidFileSize(10 * 1024 * 1024)).toBe(true);
        expect(isValidFileSize(49 * 1024 * 1024)).toBe(true);
      });

      it("accepts file exactly at limit", () => {
        expect(isValidFileSize(50 * 1024 * 1024)).toBe(true);
      });

      it("rejects files over 50MB", () => {
        expect(isValidFileSize(50 * 1024 * 1024 + 1)).toBe(false);
        expect(isValidFileSize(100 * 1024 * 1024)).toBe(false);
      });
    });
  });
});

// ---------------------------------------------------------------------------
// Indexing Status Display Tests
// ---------------------------------------------------------------------------

describe("Indexing Status", () => {
  /**
   * Get status display info based on indexed_at.
   */
  const getStatusInfo = (
    indexedAt?: string,
  ): { label: string; color: string } => {
    if (indexedAt) {
      return { label: "Indexed", color: "success" };
    }
    return { label: "Pending", color: "warning" };
  };

  it("returns Indexed status for indexed documents", () => {
    const status = getStatusInfo("2024-01-01T00:00:00Z");
    expect(status.label).toBe("Indexed");
    expect(status.color).toBe("success");
  });

  it("returns Pending status for non-indexed documents", () => {
    const status = getStatusInfo(undefined);
    expect(status.label).toBe("Pending");
    expect(status.color).toBe("warning");
  });

  it("returns Pending status for empty string", () => {
    const status = getStatusInfo("");
    expect(status.label).toBe("Pending");
    expect(status.color).toBe("warning");
  });
});

// ---------------------------------------------------------------------------
// Upload New Document Version
// ---------------------------------------------------------------------------

describe("Upload new document version", () => {
  const policy = buildDocument({
    id: "workspace-1/doc-1",
    filename: "Policy.docx",
    index_status: "completed",
    version: 1,
  });

  const selectVersionFile = async (file: File) => {
    render(<Documents />);
    fireEvent.click(
      await screen.findByRole("button", {
        name: "Upload new version of Policy.docx",
      }),
    );
    fireEvent.change(screen.getByTestId("version-file-input"), {
      target: { files: [file] },
    });
  };

  beforeEach(() => {
    mockListDocuments.mockResolvedValue([policy]);
  });

  it("uploads the chosen file as the next version and reloads the list", async () => {
    mockUploadDocumentVersion.mockResolvedValue({
      success: true,
      new_doc_id: "workspace-1/doc-2",
      new_version: 2,
      filename: "Policy.docx",
      folder: "/",
      index_status: "pending",
    });
    const file = new File(["new content"], "Policy final.docx");

    await selectVersionFile(file);

    await waitFor(() =>
      expect(mockUploadDocumentVersion).toHaveBeenCalledWith(
        "workspace-1",
        "workspace-1/doc-1",
        file,
      ),
    );
    expect(
      await screen.findByText("Uploaded Policy.docx as version 2"),
    ).toBeInTheDocument();
    await waitFor(() => expect(mockListDocuments).toHaveBeenCalledTimes(2));
  });

  it("refuses a file of a different type without uploading", async () => {
    await selectVersionFile(new File(["pdf"], "Policy.pdf"));

    expect(
      await screen.findByText(
        "A new version of Policy.docx must be a .docx file.",
      ),
    ).toBeInTheDocument();
    expect(mockUploadDocumentVersion).not.toHaveBeenCalled();
  });

  it("opens version history from the v1 label", async () => {
    mockGetVersionHistory.mockResolvedValue({
      original_doc_id: "workspace-1/doc-1",
      versions: [],
      total: 0,
      current_version: 1,
    });
    render(<Documents />);

    fireEvent.click(await screen.findByRole("button", { name: "v1" }));

    await waitFor(() =>
      expect(mockGetVersionHistory).toHaveBeenCalledWith(
        "workspace-1",
        "workspace-1/doc-1",
      ),
    );
  });
});
